import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, InputLabel, FormControl, IconButton } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

const ChatInput = ({ 
  type = 'text', 
  onSendMessage, 
  inputLabel = "Type your message and press Enter to send...",
  options = []
}) => {
  const [userInput, setUserInput] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const inputRef = useRef(null);
  const feetRef = useRef(null);
  const inchesRef = useRef(null);

  useEffect(() => {
    if (type === 'height') {
      if (heightFeet.length === 0) {
        feetRef.current?.focus();
      } else if (heightInches.length === 0) {
        inchesRef.current?.focus();
      } else {
        onSendMessage(`${heightFeet}ft ${heightInches}inches`);
      }
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [type, heightFeet, heightInches, onSendMessage]);

  const handleInputChange = (e) => setUserInput(e.target.value);

  const handleNumericInputChange = (e, min, max) => {
    const value = e.target.value;
    if (value === '' || (parseFloat(value) >= min && parseFloat(value) <= max)) {
      setUserInput(value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = () => {
    if (type === 'height') {
      if (heightFeet && heightInches) {
        onSendMessage(`${heightFeet}ft ${heightInches}inches`);
        setHeightFeet('');
        setHeightInches('');
      }
    } else if (userInput.trim()) {
      onSendMessage(userInput);
      setUserInput('');
    }
  };

  const renderInput = () => {
    const commonProps = {
      fullWidth: true,
      variant: "outlined",
      value: userInput,
      onChange: handleInputChange,
      onKeyPress: handleKeyPress,
      placeholder: inputLabel,
      inputRef: inputRef,
    };

    const renderSendButton = () => (
      <IconButton 
        onClick={sendMessage}
        aria-label="send"
        sx={{ flexShrink: 0, ml: 1 }}
      >
        <SendIcon />
      </IconButton>
    );

    switch (type) {
      case 'number':
      case 'weight':
      case 'age':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <TextField 
              {...commonProps} 
              type="number" 
              inputProps={{
                min: type === 'weight' ? 1 : type === 'age' ? 1 : undefined,
                max: type === 'weight' ? 680 : type === 'age' ? 120 : undefined,
                step: type === 'weight' ? "0.1" : undefined
              }}
              onChange={(e) => handleNumericInputChange(e, 
                type === 'weight' ? 1 : type === 'age' ? 1 : -Infinity, 
                type === 'weight' ? 680 : type === 'age' ? 120 : Infinity
              )}
              sx={{ flexGrow: 1 }}
            />
            {renderSendButton()}
          </Box>
        );
      case 'height':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <FormControl variant="outlined" sx={{ flexGrow: 1, mr: 1 }}>
              <InputLabel>Feet</InputLabel>
              <Select
                value={heightFeet}
                onChange={(e) => setHeightFeet(e.target.value)}
                label="Feet"
                inputRef={feetRef}
              >
                {[...Array(8)].map((_, i) => (
                  <MenuItem key={i + 1} value={(i + 1).toString()}>
                    {i + 1} ft
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" sx={{ flexGrow: 1 }}>
              <InputLabel>Inches</InputLabel>
              <Select
                value={heightInches}
                onChange={(e) => setHeightInches(e.target.value)}
                label="Inches"
                inputRef={inchesRef}
              >
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i} value={i.toString()}>
                    {i} in
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {renderSendButton()}
          </Box>
        );
      case 'select':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <FormControl fullWidth variant="outlined" sx={{ flexGrow: 1 }}>
              <InputLabel>{inputLabel}</InputLabel>
              <Select
                {...commonProps}
                label={inputLabel}
              >
                {options.map((option, index) => (
                  <MenuItem key={index} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {renderSendButton()}
          </Box>
        );
      default:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <TextField 
              {...commonProps} 
              multiline 
              maxRows={4} 
              sx={{ flexGrow: 1 }}
            />
            {renderSendButton()}
          </Box>
        );
    }
  };

  return (
    <Box width="100%" display="flex" alignItems="center">
      {renderInput()}
    </Box>
  );
};

export default ChatInput;